import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Mail, Phone, MapPin, Heart } from 'lucide-react';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "/lane4-logo.png";

const Footer = () => {
  const [email, setEmail] = useState('');
  const [subscribeStatus, setSubscribeStatus] = useState(null);
  // Spam defences: invisible to people, and a timestamp so the server can reject
  // submissions that arrive faster than anyone could type.
  const [honeypot, setHoneypot] = useState('');
  const formLoadedAt = useRef(Date.now());

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;

    try {
      await axios.post(`${API}/newsletter`, {
        email,
        website: honeypot,
        form_loaded_at: formLoadedAt.current,
      });
      setSubscribeStatus('success');
      setEmail('');
    } catch (error) {
      if (error.response?.status === 400) {
        setSubscribeStatus('already');
      } else if (error.response?.status === 429) {
        setSubscribeStatus('rate_limited');
      } else {
        setSubscribeStatus('error');
      }
    }
    setTimeout(() => setSubscribeStatus(null), 4000);
  };

  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    { name: 'Our Programs', path: '/programs' },
    { name: 'Impact', path: '/impact' },
    { name: 'Events', path: '/events' },
  ];

  const getInvolvedLinks = [
    { name: 'Donate', path: '/donate' },
    { name: 'Volunteer', path: '/get-involved' },
    { name: 'Become a Sponsor', path: '/sponsors' },
    { name: 'Contact Us', path: '/contact' },
  ];

  const socialLinks = [
    { icon: Facebook, href: 'https://www.facebook.com/share/1DLKoS9hos/', label: 'Facebook' },
    { icon: Instagram, href: 'https://www.instagram.com/lane4foundation', label: 'Instagram' },
  ];

  return (
    <footer className="bg-slate-900 text-white" data-testid="footer">
      {/* Newsletter Section */}
      <div className="bg-red-600 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <h3 className="font-heading text-2xl font-bold text-white">Stay Connected</h3>
              <p className="text-red-100 mt-1">Get updates on our impact and upcoming events</p>
            </div>
            <form onSubmit={handleSubscribe} className="w-full md:w-auto">
              {/* Honeypot: bots fill every field they find; positioned offscreen
                  rather than display:none, which some bots deliberately skip. */}
              <input
                type="text"
                name="website"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                style={{ position: 'absolute', left: '-9999px' }}
                data-testid="newsletter-honeypot"
              />
              <div className="flex items-center gap-2" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-white text-slate-900 border-0 h-12 flex-1 min-w-[200px] md:min-w-[280px]"
                  style={{ flex: 1, height: '48px' }}
                  data-testid="newsletter-email-input"
                />
                <Button
                  type="submit"
                  className="bg-slate-900 text-white hover:bg-slate-800 h-12 px-6 whitespace-nowrap"
                  style={{ height: '48px', flexShrink: 0 }}
                  data-testid="newsletter-submit-btn"
                >
                  Subscribe
                </Button>
              </div>
            </form>
          </div>
          {subscribeStatus && (
            <p className={`mt-4 text-center md:text-right text-sm ${subscribeStatus === 'success' ? 'text-white' : 'text-red-200'}`}>
              {subscribeStatus === 'success' && 'Thank you for subscribing!'}
              {subscribeStatus === 'already' && 'This email is already subscribed.'}
              {subscribeStatus === 'rate_limited' && 'Too many attempts. Please try again later.'}
              {subscribeStatus === 'error' && 'Something went wrong. Please try again.'}
            </p>
          )}
        </div>
      </div>

      {/* Main Footer */}
      <div className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {/* Brand Column */}
            <div className="lg:col-span-1">
              <img
                src={LOGO_URL}
                alt="Lane 4 For All Kids Foundation"
                className="h-16 w-auto mb-6 brightness-0 invert"
              />
              <p className="text-slate-400 text-sm leading-relaxed">
                Removing financial barriers in youth athletics so every child has access to opportunity, regardless of their background.
              </p>
              <div className="flex space-x-4 mt-6">
                {socialLinks.map((social) => (
                  <a
                    key={social.label}
                    href={social.href}
                    className="w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                    aria-label={social.label}
                    data-testid={`social-${social.label.toLowerCase()}`}
                  >
                    <social.icon className="w-5 h-5" />
                  </a>
                ))}
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-heading text-lg font-bold uppercase tracking-wider mb-6">Quick Links</h4>
              <ul className="space-y-3">
                {quickLinks.map((link) => (
                  <li key={link.path}>
                    <Link
                      to={link.path}
                      className="text-slate-400 hover:text-red-500 transition-colors text-sm"
                      data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Get Involved */}
            <div>
              <h4 className="font-heading text-lg font-bold uppercase tracking-wider mb-6">Get Involved</h4>
              <ul className="space-y-3">
                {getInvolvedLinks.map((link) => (
                  <li key={link.path}>
                    <Link
                      to={link.path}
                      className="text-slate-400 hover:text-red-500 transition-colors text-sm"
                      data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="font-heading text-lg font-bold uppercase tracking-wider mb-6">Contact Us</h4>
              <ul className="space-y-4">
                <li className="flex items-start space-x-3">
                  <MapPin className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-400 text-sm">
                    PO BOX 14076<br />
                    Saint Petersburg, FL 33733
                  </span>
                </li>
                <li className="flex items-center space-x-3">
                  <Phone className="w-5 h-5 text-red-500 flex-shrink-0" />
                  <a href="tel:+18774952634" className="text-slate-400 hover:text-red-500 text-sm">
                    877.495.2634
                  </a>
                </li>
                <li className="flex items-center space-x-3">
                  <Mail className="w-5 h-5 text-red-500 flex-shrink-0" />
                  <a href="mailto:info@lane4foundation.com" className="text-slate-400 hover:text-red-500 text-sm">
                    info@lane4foundation.com
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-800 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-slate-500 text-sm text-center md:text-left">
              <p>© {new Date().getFullYear()} Lane 4 For All Kids Foundation, Inc. All rights reserved.</p>
              <p className="mt-1 text-xs">
                EIN: XX-XXXXXXX | 501(c)(3) status pending
              </p>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-sm">
              <Link to="/terms" className="text-slate-500 hover:text-red-500">Terms & Conditions</Link>
              <Link to="/privacy" className="text-slate-500 hover:text-red-500">Privacy Policy</Link>
              <Link to="/transparency-policy" className="text-slate-500 hover:text-red-500">Transparency Policy</Link>
              <Link to="/refund-policy" className="text-slate-500 hover:text-red-500">Refund Policy</Link>
              <Link to="/where-donations-go" className="text-slate-500 hover:text-red-500">Where Donations Go</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
